import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  FolderKanban,
  Plus,
  ChevronRight,
  Filter,
  Loader2
} from 'lucide-react';
import apiService from './apiService';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const ProjectList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProjects, setTotalProjects] = useState(0);

  // Fetch projects when filters change
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await apiService.getProjects({
          status: statusFilter,
          search: searchQuery
        });
        setProjects(response.projects);
        setTotalProjects(response.total);
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(fetchProjects, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, statusFilter]);

  const filteredProjects = projects;

  const getStatusBadge = (status) => {
    const statusMap = {
      active: 'aut-badge-active',
      pending: 'aut-badge-pending',
      completed: 'aut-badge-completed',
      archived: 'aut-badge-archived'
    };
    return statusMap[status] || 'aut-badge-active';
  };

  return (
    <div className="aut-project-list">
      <div className="aut-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="aut-page-title">Projects</h1>
          <p className="aut-page-description">Manage and track all your projects in one place.</p>
        </div>
        <Link to="/create-project" className="aut-btn aut-btn-primary">
          <Plus size={18} />
          <span>New Project</span>
        </Link>
      </div>

      {/* Filters */}
      <div className="aut-section">
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div className="aut-search-container" style={{ flex: 1, maxWidth: '400px' }}>
            <Search size={18} className="aut-search-icon" />
            <input
              type="text"
              className="aut-input aut-search-input"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={18} style={{ color: 'var(--aut-text-muted)' }} />
            <select
              className="aut-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: 'auto', minWidth: '140px' }}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Project List */}
      <div className="aut-list">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
            <Loader2 size={32} className="aut-spinner" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : filteredProjects.length > 0 ? (
          filteredProjects.map((project) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="aut-list-item"
            >
              <div className="aut-list-item-content">
                <div className="aut-list-item-icon">
                  <FolderKanban size={22} />
                </div>
                <div className="aut-list-item-info">
                  <h3 className="aut-list-item-title">{project.name}</h3>
                  <p className="aut-list-item-subtitle">{project.description}</p>
                </div>
              </div>
              <div className="aut-list-item-meta">
                <div className="aut-list-item-meta-item">
                  <div className="aut-list-item-meta-label">Status</div>
                  <span className={`aut-badge ${getStatusBadge(project.status)}`}>
                    {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                  </span>
                </div>
                <div className="aut-list-item-meta-item">
                  <div className="aut-list-item-meta-label">Progress</div>
                  <div className="aut-list-item-meta-value">
                    {project.tasks.completed}/{project.tasks.total} tasks
                  </div>
                </div>
                <div className="aut-list-item-meta-item">
                  <div className="aut-list-item-meta-label">Due</div>
                  <div className="aut-list-item-meta-value">{project.dueDate}</div>
                </div>
                <ChevronRight size={20} style={{ color: 'var(--aut-text-muted)' }} />
              </div>
            </Link>
          ))
        ) : (
          <div className="aut-empty-state">
            <div className="aut-empty-state-icon">
              <FolderKanban size={48} />
            </div>
            <h3 className="aut-empty-state-title">No projects found</h3>
            <p className="aut-empty-state-description">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first project'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Link to="/create-project" className="aut-btn aut-btn-primary">
                <Plus size={18} />
                <span>Create Project</span>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Results count */}
      {!loading && filteredProjects.length > 0 && (
        <div style={{ marginTop: '24px', color: 'var(--aut-text-muted)', fontSize: '14px' }}>
          Showing {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default ProjectList;
